/**
 * Veo 3.0 视频生成（通过后端服务器调用 Google Veo API）
 *
 * POST /web/v1/veo/v3-0/createVideo   — 提交生成任务
 * POST /web/v1/veo/v3-0/getTaskResult — 查询任务结果
 *
 * 认证方式：Bearer token（由 apiClient 自动注入）
 */
import apiClient from '../../../config'

var POLL_INTERVAL = 10000
var POLL_TIMEOUT = 10 * 60 * 1000

var CREATE_PATH = '/veo/v3-0/createVideo'
var RESULT_PATH = '/veo/v3-0/getTaskResult'

/**
 * 获取用户登录凭证
 */
function getMiToken() {
  return localStorage.getItem('token') || ''
}

/**
 * 构建提交参数（与前端页面参数一致）
 */
function buildSubmitParams(params) {
  var token = getMiToken()
  var result = {
    miToken: token,
    prompt: (params.prompt || '').trim().slice(0, 2000),
    videoDuration: params.videoDuration || 8,
    videoRes: params.videoRes || '1080p',
    aspectRatio: params.aspectRatio || '16:9',
    generateAudio: params.generateAudio !== false
  }

  // 负面提示词
  var negativePrompt = (params.negativePrompt || '').trim().slice(0, 2000)
  if (negativePrompt) {
    result.negativePrompt = negativePrompt
  }

  // 种子值
  var seed = params.seed != null && params.seed !== '' && params.seed !== -1 ? parseInt(params.seed, 10) : null
  if (seed != null && !isNaN(seed)) {
    result.seed = seed
  }

  // 图生视频：参考图片 URL
  var hasImage = params.referenceImage && typeof params.referenceImage === 'string' && params.referenceImage.trim().length > 0
  if (hasImage) {
    result.referenceImage = params.referenceImage.trim()
  }

  return { params: result, hasImage: hasImage }
}

/**
 * 提交 Veo 3.0 视频生成任务
 * @param {Object} params - 生成参数
 * @returns {Promise<{taskId: string, videoMode: string}>}
 */
export function submitGoogleVeo3Task(params) {
  var prompt = (params.prompt || '').trim()
  if (!prompt) return Promise.reject(new Error('prompt 必填'))

  var result = buildSubmitParams(params)

  return apiClient.post(CREATE_PATH, result.params).then(function (data) {
    var resData = data && data.data
    var taskId = resData && resData.taskId ? String(resData.taskId) : ''
    if (!taskId) return Promise.reject(new Error('后端接口未返回 taskId'))
    return {
      taskId: taskId,
      videoMode: result.hasImage ? 'image' : (params.videoMode || 'text')
    }
  })
}

/**
 * 解析后端返回的任务状态
 */
function parseTaskStatus(data) {
  var status = data && data.status ? String(data.status).toLowerCase() : ''
  if (status === 'succeed' || status === 'completed') {
    return { status: 'completed', url: data.videoUrl || null, error: null }
  }
  if (status === 'failed') {
    return { status: 'failed', url: null, error: data.message || '生成失败' }
  }
  // processing / pending / queued 等
  return { status: 'processing', url: null, error: null }
}

/**
 * 轮询 Veo 3.0 任务状态
 * @param {string} taskId - 创建任务返回的 taskId
 * @param {function} callback - 回调函数 (err, { status, url, error })
 * @param {number} interval - 轮询间隔（毫秒）
 */
export function pollGoogleVeo3Task(taskId, callback, interval) {
  var pollInterval = interval || POLL_INTERVAL
  var deadline = Date.now() + POLL_TIMEOUT
  var pollTimer = null
  var stopped = false
  var resolvingSuccess = false

  function stop() {
    stopped = true
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  }

  function poll() {
    if (stopped) return
    apiClient
      .post(RESULT_PATH, { miToken: getMiToken(), taskId: taskId })
      .then(function (data) {
        if (stopped) return
        var resData = data && data.data
        if (!resData) {
          if (typeof callback === 'function') callback(new Error('查询结果为空'), null)
          stop()
          return
        }
        var parsed = parseTaskStatus(resData)

        if (parsed.status === 'completed') {
          if (resolvingSuccess) return
          resolvingSuccess = true
          if (typeof callback === 'function') {
            callback(null, { status: 'completed', url: parsed.url, error: null })
          }
          stop()
          return
        }

        if (parsed.status === 'failed') {
          if (typeof callback === 'function') callback(null, { status: 'failed', url: null, error: parsed.error })
          stop()
          return
        }

        // 处理中
        if (typeof callback === 'function') callback(null, { status: 'processing', url: null, error: null })
        if (Date.now() > deadline) {
          if (typeof callback === 'function') callback(new Error('轮询超时'), null)
          stop()
        } else {
          pollTimer = setTimeout(poll, pollInterval)
        }
      })
      .catch(function (err) {
        if (stopped) return
        if (typeof callback === 'function') callback(err, null)
        stop()
      })
  }

  poll()
  return { stop: stop }
}
